import React, { useContext, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Button,
  IconButton,
  Grid,
  Divider,
  Chip,
  Tooltip,
  Link,
  SvgIcon,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import EditIcon from "@mui/icons-material/Edit";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../context/UserContext";
import BackBtn from "../../admin/widgets/BackBtn";
import ImageCropperModal from "../../../../widgets/ImageCropperModal";
import { API_ENDPOINT } from "../../../../constants/constants";
import { toast } from "react-toastify";
import { getUser } from "../../../../utility/utility";
import useGetEventSettings from "../../../hooks/useGetEventSettings";

const BannerContainer = styled(Box)(({ theme, banner }) => ({
  position: "relative",
  height: 200,
  backgroundColor: theme.palette.grey[300],
  backgroundImage: banner ? `url(${banner})` : "none",
  backgroundSize: "cover",
  backgroundPosition: "center",
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
}));

const BannerUploadButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const ProfileImageContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  marginTop: -50,
  marginLeft: theme.spacing(2),
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 100,
  height: 100,
  border: `4px solid ${theme.palette.background.paper}`,
}));

const ProfileImageUploadButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: 0,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

const XIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 16 16">
    <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z" />
  </SvgIcon>
);

const VisitorProfileView = () => {
  const { user, loading, reloadUser } = useContext(UserContext);
  const {
    data: eD,
    isLoading: isProfileLoading,
    isError,
    error,
  } = useGetEventSettings();
  const navigate = useNavigate();
  const [uploadType, setUploadType] = useState();
  const [cropperOpen, setCropperOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  if (loading || isProfileLoading) {
    return <Typography>Loading...</Typography>;
  }

  if (!user) {
    return <Typography>No user data available.</Typography>;
  }

  const handleUpdateProfile = () => {
    navigate("/visitor/edit-profile");
  };

  const handleUploadSuccess = (response) => {
    toast.success(response.message);
    reloadUser();
  };

  const socialLinks = [
    { key: "in", icon: <InstagramIcon />, label: "Instagram", url: user.in },
    { key: "x", icon: <XIcon />, label: "X (Twitter)", url: user.x },
    { key: "fb", icon: <FacebookIcon />, label: "Facebook", url: user.fb },
    { key: "ln", icon: <LinkedInIcon />, label: "LinkedIn", url: user.ln },
  ];

  return (
    <Box>
      <BackBtn />
      <ImageCropperModal
        open={cropperOpen}
        onClose={() => setCropperOpen(false)}
        aspectRatio={aspectRatio}
        maxSizeKB={1024}
        uploadUrl={`${API_ENDPOINT}user/upload-profile-image?uploadType=${uploadType}`}
        headers={{ token: getUser()?.token }}
        onSuccess={handleUploadSuccess}
        onError={(error) => console.error("Upload error:", error)}
      />

      <Box sx={{ maxWidth: 800, mx: "auto", my: 2 }}>
        <Card elevation={3}>
          {/* Banner */}
          <BannerContainer banner={user?.profileBanner || eD?.defProfileBanner}>
            <Tooltip title="Change Banner">
              <BannerUploadButton
                onClick={() => {
                  setAspectRatio(3);
                  setUploadType("profileBanner");
                  setCropperOpen(true);
                }}
              >
                <PhotoCameraIcon />
              </BannerUploadButton>
            </Tooltip>
          </BannerContainer>

          {/* Profile Image */}
          <ProfileImageContainer>
            <ProfileAvatar src={user.profileImage} alt={user.firstName}>
              {user.firstName?.charAt(0)}
            </ProfileAvatar>
            <Tooltip title="Change Profile Image">
              <ProfileImageUploadButton
                onClick={() => {
                  setAspectRatio(1);
                  setUploadType("profileImage");
                  setCropperOpen(true);
                }}
              >
                <PhotoCameraIcon fontSize="small" />
              </ProfileImageUploadButton>
            </Tooltip>
          </ProfileImageContainer>

          {/* Profile Details */}
          <CardContent>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
              <Box>
                <Typography variant="h5" component="div">
                  {user.title} {user.firstName}
                </Typography>
                {user.designation && (
                  <Typography
                    variant="subtitle1"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {user.designation}
                  </Typography>
                )}
                {user.barcode_id && (
                  <Chip
                    label={user.barcode_id}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>

              <Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={handleUpdateProfile}
                >
                  Update Profile
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {/* User Info */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Username
                </Typography>
                <Typography variant="body1">{user.username}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Mobile No
                </Typography>
                <Typography variant="body1">{user.mobileNo}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{user.companyName}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Industry Sector
                </Typography>
                <Typography variant="body1">{user.industrySector}</Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Department
                </Typography>
                <Typography variant="body1">{user.department}</Typography>
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Address
                </Typography>
                <Typography variant="body1">
                  {user.address}, {user.city?.name}, {user.state?.name},{" "}
                  {user.country?.name}, {user.pinzip}
                </Typography>
              </Grid>

              {/* Event source */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  How did you find out about this event?
                </Typography>
                <Typography variant="body1">
                  {user.howDidYouFindOutAboutThisEvent}
                </Typography>
              </Grid>

              {/* Product Categories (Subcategories only) */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Product Categories
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {user.selectProductCategoriesChooseMin10Products?.map(
                    (cat, i) => (
                      <Chip
                        key={i}
                        label={cat.SubCategory || cat.subSubCat}
                        variant="outlined"
                      />
                    )
                  )}
                </Box>
              </Grid>

              {/* Social Media Links */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  Social Media
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                  {socialLinks.map(({ key, icon, label, url }) =>
                    url ? (
                      <Tooltip key={key} title={label}>
                        <IconButton
                          component={Link}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="primary"
                          size="small"
                        >
                          {icon}
                        </IconButton>
                      </Tooltip>
                    ) : null
                  )}
                </Box>
              </Grid>

              {/* QR Code */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  QR Code
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <img
                    src={user.qrLink}
                    alt="QR Code"
                    style={{ maxWidth: 150, height: "auto" }}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default VisitorProfileView;
